import { defineStore } from 'pinia';
const DEFAULT_PANEL_SIZE = 50;
const CASCADE_STEP = 4;
export function defaultPanelPosition(index) {
    const slot = index % 4;
    const cascade = Math.floor(index / 4) * CASCADE_STEP;
    const max = 100 - DEFAULT_PANEL_SIZE;
    return {
        x: Math.min(max, (slot % 2) * DEFAULT_PANEL_SIZE + cascade),
        y: Math.min(max, Math.floor(slot / 2) * DEFAULT_PANEL_SIZE + cascade),
        width: DEFAULT_PANEL_SIZE,
        height: DEFAULT_PANEL_SIZE
    };
}
export function defaultScene(id = 'scene-1', title = 'Sahne 1') {
    return { id, title, narrative: '', panels: [] };
}
export function docToScenes(doc) {
    if (Array.isArray(doc.scenes) && doc.scenes.length) {
        return doc.scenes.map((s) => ({
            id: s.id || 'scene-1',
            title: s.title || 'Sahne',
            narrative: s.narrative || '',
            panels: Array.isArray(s.panels) ? s.panels : [],
        }));
    }
    const panels = Array.isArray(doc.panels) ? doc.panels : [];
    return [{ ...defaultScene('scene-1'), panels }];
}
export const useCanvasStore = defineStore('canvas', {
    state: () => ({
        scenes: [defaultScene()],
        activeSceneId: 'scene-1',
        layout: 'freeform',
        selectedPanelId: null,
        dashboardId: null,
        dashboardName: '',
        params: {},
        dirty: false,
        _suppressDirty: false,
        filters: [],
        focusPanelId: null,
        lastMapClick: null,
    }),
    getters: {
        activeScene(state) {
            return state.scenes.find((s) => s.id === state.activeSceneId) || state.scenes[0];
        },
        panels() {
            const scene = this.activeScene;
            return scene?.panels ?? [];
        },
        getPanelById: (state) => (id) => {
            for (const scene of state.scenes) {
                const panel = scene.panels.find((p) => p.id === id);
                if (panel)
                    return panel;
            }
            return undefined;
        },
        panelCount() {
            return this.panels.length;
        },
        findScene: (state) => (sceneId) => state.scenes.find((s) => s.id === sceneId),
    },
    actions: {
        markDirty() {
            if (!this._suppressDirty)
                this.dirty = true;
        },
        setActiveScene(sceneId, options) {
            if (!this.findScene(sceneId))
                return false;
            this.activeSceneId = sceneId;
            this.selectedPanelId = null;
            if (!options?.silent)
                this.markDirty();
            return true;
        },
        addScene(title) {
            const existingIds = new Set(this.scenes.map((s) => s.id));
            let n = this.scenes.length + 1;
            let id = `scene-${n}`;
            while (existingIds.has(id)) {
                n += 1;
                id = `scene-${n}`;
            }
            const scene = defaultScene(id, (title || `Sahne ${n}`).trim() || `Sahne ${n}`);
            this.scenes.push(scene);
            this.activeSceneId = id;
            this.selectedPanelId = null;
            this.markDirty();
            return scene;
        },
        setSceneTitle(sceneId, title) {
            const scene = this.findScene(sceneId);
            if (!scene)
                return false;
            scene.title = String(title ?? '').trim() || scene.title;
            this.markDirty();
            return true;
        },
        setSceneNarrative(sceneId, text) {
            const scene = this.findScene(sceneId);
            if (!scene)
                return false;
            scene.narrative = String(text ?? '');
            this.markDirty();
            return true;
        },
        moveScene(sceneId, direction) {
            const idx = this.scenes.findIndex((s) => s.id === sceneId);
            if (idx < 0)
                return false;
            const next = idx + direction;
            if (next < 0 || next >= this.scenes.length)
                return false;
            const copy = [...this.scenes];
            const [item] = copy.splice(idx, 1);
            copy.splice(next, 0, item);
            this.scenes = copy;
            this.markDirty();
            return true;
        },
        deleteScene(sceneId) {
            if (this.scenes.length <= 1)
                return false;
            const idx = this.scenes.findIndex((s) => s.id === sceneId);
            if (idx < 0)
                return false;
            this.scenes.splice(idx, 1);
            if (this.activeSceneId === sceneId) {
                this.activeSceneId = this.scenes[Math.min(idx, this.scenes.length - 1)].id;
            }
            this.selectedPanelId = null;
            this.markDirty();
            return true;
        },
        applyDocScenes(doc, layout) {
            this.scenes = docToScenes(doc);
            this.activeSceneId = this.scenes[0].id;
            this.selectedPanelId = null;
            if (layout)
                this.layout = layout;
        },
        createPanel(id, options) {
            const panelId = id || this.getNextAvailableId();
            const existingPanel = this.panels.find((p) => p.id === panelId);
            if (existingPanel) {
                console.warn(`Panel ${panelId} zaten mevcut, güncelleniyor`);
                this.updatePanel(panelId, options || {});
                return existingPanel;
            }
            const newPanel = {
                id: panelId,
                title: options?.title?.trim() || '',
                widgetType: options?.widgetType || null,
                data: options?.data || null,
                position: options?.position || defaultPanelPosition(this.panels.length),
                toolCalls: options?.toolCalls || [],
                source: options?.source,
                recipe: options?.recipe,
            };
            this.panels.push(newPanel);
            this.markDirty();
            return newPanel;
        },
        deletePanel(id) {
            const index = this.panels.findIndex((p) => p.id === id);
            if (index !== -1) {
                this.panels.splice(index, 1);
                if (this.selectedPanelId === id)
                    this.selectedPanelId = null;
                this.markDirty();
                return true;
            }
            return false;
        },
        findPanel(id) {
            return this.panels.find((p) => p.id === id);
        },
        updatePanel(id, updates) {
            const panel = this.findPanel(id);
            if (panel) {
                if (typeof updates.title === 'string') {
                    updates = { ...updates, title: updates.title.trim() };
                }
                Object.assign(panel, updates);
                this.markDirty();
                return true;
            }
            return false;
        },
        setPanelTitle(id, title) {
            return this.updatePanel(id, { title: String(title ?? '').trim() });
        },
        selectPanel(id) {
            this.selectedPanelId = id;
        },
        clearAllPanels() {
            const scene = this.activeScene;
            if (scene)
                scene.panels = [];
            this.selectedPanelId = null;
            this.markDirty();
        },
        getNextAvailableId() {
            const existing = this.scenes.flatMap((s) => s.panels.map((p) => p.id));
            for (let i = 1; i <= 20; i++) {
                const id = `panel-${i}`;
                if (!existing.includes(id))
                    return id;
            }
            return `panel-${Date.now()}`;
        },
        setLayout(layout) {
            this.layout = layout;
        },
        async save(name) {
            const { saveDashboardToApi } = await import('@/chat/packages/storyboardDashboard');
            const displayName = (name || this.dashboardName || 'Dashboard').trim();
            const row = await saveDashboardToApi({
                dashboardId: this.dashboardId,
                name: displayName,
                layout: this.layout,
                scenes: this.scenes,
                params: this.params,
            });
            this.dashboardId = row.id;
            this.dashboardName = row.name;
            this.dirty = false;
            return row;
        },
        async load(dashboardId) {
            const { loadDashboardFromApi } = await import('@/chat/packages/storyboardDashboard');
            this._suppressDirty = true;
            try {
                const row = await loadDashboardFromApi(dashboardId);
                const doc = row.doc || {};
                this.dashboardId = row.id;
                this.dashboardName = row.name || '';
                this.params = row.params || {};
                this.applyDocScenes(doc, doc.layout || 'freeform');
                this.dirty = false;
                return row;
            }
            finally {
                this._suppressDirty = false;
            }
        },
        async refreshPanel(panelId) {
            const panel = this.findPanel(panelId);
            if (!panel?.recipe) {
                return { success: false, error: 'Tarif yok' };
            }
            const { refreshPanelByRecipe } = await import('@/chat/packages/storyboardDashboard');
            return refreshPanelByRecipe(panel, this.params);
        },
        async refreshAll() {
            const { refreshAllRecipePanels } = await import('@/chat/packages/storyboardDashboard');
            return refreshAllRecipePanels(this.params);
        },
        setFilter(field, value, sourcePanelId) {
            const f = String(field || '').trim();
            if (!f)
                return;
            const existing = this.filters.findIndex((item) => item.field.toLowerCase() === f.toLowerCase() && item.value === value);
            const entry = { field: f, value, sourcePanelId };
            if (existing >= 0)
                this.filters.splice(existing, 1, entry);
            else
                this.filters.push(entry);
            this.markDirty();
        },
        clearFilters() {
            if (!this.filters.length)
                return;
            this.filters = [];
            this.markDirty();
        },
        removeFilter(index) {
            if (index < 0 || index >= this.filters.length)
                return;
            this.filters.splice(index, 1);
            this.markDirty();
        },
        recordMapClick(payload) {
            this.lastMapClick = payload;
        },
        applyMapClickFilter(panelId, properties, layerType) {
            const name = properties.stop_name ??
                properties.name ??
                properties.mahalle ??
                properties.ilce;
            if (name == null || String(name).trim() === '')
                return;
            let field = 'name';
            if (properties.stop_name != null)
                field = 'stop_name';
            else if (properties.mahalle != null)
                field = 'mahalle';
            else if (properties.ilce != null)
                field = 'ilce';
            this.setFilter(field, String(name).trim(), panelId);
            this.recordMapClick({ panelId, layerType, properties });
        },
        setFocusPanel(panelId) {
            this.focusPanelId = panelId;
        },
    },
});
